"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/hooks/use-auth";
import { toSentenceCase } from "@/utils/string/to-sentence-case";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { PostPropertyLayout } from "./components/layout/post-property-layout";
import { FormProvider, useFormContext } from "./context/FormProvider";
import { useFormMutation } from "./hooks/useFormMutation";
import { useSellFlowData } from "./hooks/useSellFlowData";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { getSteps } from "./steps/config";
import type { StepKey } from "./types/form";

// Step Components
import { trackSellerListing } from "@/utils/analytics/track-seller-listing";
import { logger } from "@/utils/logger";
import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { BuilderOrLandownerOrInvestorProject } from "./builder-landowner-investor-project";
import { StepProfile } from "./step-1-profile";
import { StepAddress } from "./step-2-address";
import { StepBasic } from "./step-3-basic";
import { StepAdditional } from "./step-4-additional";
import { StepProjectOrCommunity } from "./step-5-other";
import { StepUploads } from "./step-6-uploads";
import { StepFeeConfirmation } from "./step-7-fee";
import { StepSlotBooking } from "./step-8-slot";
import { StepSuccess } from "./step-9-success";
const stepComponents = {
  profile: StepProfile,
  address: StepAddress,
  basic: StepBasic,
  additional: StepAdditional,
  other: StepProjectOrCommunity,
  uploads: StepUploads,
  fee: StepFeeConfirmation,
  slot: StepSlotBooking,
  success: StepSuccess,
  builderOrLandownerOrInvestorProject: BuilderOrLandownerOrInvestorProject
} as const;
export function PostPropertyFlow() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <FormProvider><PostPropertyFlowContent /></FormProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PostPropertyFlowContent() {
  const {
    state
  } = useFormContext();
  const {
    user,
    isAuthenticated,
    isLoading
  } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawUnitId = searchParams?.get("rawUnitId") as string | null || null;
  const urlGroupId = searchParams?.get("groupId") as string | null || null;
  const sellerUnitId = searchParams?.get("sellerUnitId") as string | null || null;
  const process = searchParams?.get("process") || null;

  // Get relationship from state to determine step configuration
  // Default to 'owner' if not set and we have a unit ID to show full progress stepper on load
  // This prevents the "3-steps then 9-steps" flickering
  const relationship = state.profile?.relationship || (sellerUnitId || rawUnitId ? "owner" : "");
  const groupType = ["owner", "agent"].includes(relationship) ? "individual" : relationship;

  // Priority: URL groupId > store selectedGroupId > first matching group from user.groups
  const sellerGroupId = urlGroupId || user?.groups.find(group => group.relationship === "seller" && group.type === groupType)?.id || "";
  const isBLOGroup = Boolean(sellerGroupId);
  const stepConfigs = getSteps({
    relationship
  });
  const {
    currentStep,
    stepConfig,
    navigationState,
    navigateNext,
    navigateBack,
    navigateToStep
  } = useStepNavigation(stepConfigs);
  // Custom hook for clean data management
  const {
    isInitializing,
    initializationError
  } = useSellFlowData(rawUnitId, sellerGroupId, isBLOGroup, sellerUnitId);

  // Memoize mutation hook to prevent unnecessary re-renders
  const stepKey = stepConfig.key as StepKey;
  const currentStepData = state[stepKey];
  const {
    mutate: saveFormData,
    loading: isSaving
  } = useFormMutation(stepKey, currentStepData, sellerGroupId);
  useEffect(() => {
    trackSellerListing({
      action: "view",
      stepKey: stepKey
    });
  }, [stepKey, sellerUnitId]);
  const handleNext = useCallback(async (stepData?: unknown) => {
    const dataToSave = stepData || currentStepData;
    trackSellerListing({
      action: "submit",
      stepKey: stepKey,
      listingId: rawUnitId ? rawUnitId : undefined
    });
    try {
      // For first step without rawUnitId - create new rawUnit
      if (currentStep === 1 && (!rawUnitId || !sellerGroupId)) {
        let result;
        try {
          result = await saveFormData(true, dataToSave);
        } catch (error_0) {
          toast.error(`Error saving form data: ${(error_0 as Error).message}`);
          logger.error(`Error saving form data: ${(error_0 as Error).message}`, {
            error: error_0,
            stepKey,
            rawUnitId,
            sellerUnitId
          });
        }
        if (result?.id || result?.groupId) {
          // Check if user is authenticated before proceeding to next step
          if (isAuthenticated) {
            // Use the efficient step config to determine next step
            const {
              getNextStepProcess
            } = await import("./steps/config");
            const nextProcess = getNextStepProcess({
              relationship,
              currentStepKey: stepKey
            });
            const finalRawUnitId = result.id || rawUnitId;
            const finalGroupId = result.groupId || sellerGroupId;
            const params = new URLSearchParams();
            if (finalRawUnitId) params.set("rawUnitId", finalRawUnitId);
            if (finalGroupId) params.set("groupId", finalGroupId);
            if (nextProcess) params.set("process", nextProcess);
            router.push(`${pathname}?${params.toString()}`);
          } else {
            // User is not authenticated, stay on current step (profile)
            const currentProcess = stepKey; // This will be 'profile' for the first step

            const finalRawUnitId_0 = result.id || rawUnitId;
            const finalGroupId_0 = result.groupId || sellerGroupId;
            const params_0 = new URLSearchParams();
            if (finalRawUnitId_0) params_0.set("rawUnitId", finalRawUnitId_0);
            if (finalGroupId_0) params_0.set("groupId", finalGroupId_0);
            params_0.set("process", currentProcess);
            router.push(`${pathname}?${params_0.toString()}`);
            toast.info("Kindly verify your phone number to proceed next step!.");
          }
        }
        return;
      }
      if (!sellerUnitId) {
        // For all other steps - update existing rawUnit
        await saveFormData(false, dataToSave);
      }
      navigateNext();
    } catch (error) {
      toast.error("Error saving form data");
      logger.error("Error saving form data", {
        stepKey,
        rawUnitId,
        sellerUnitId,
        error
      });
    }
  }, [currentStepData, currentStep, rawUnitId, sellerUnitId, sellerGroupId, isAuthenticated, navigateNext, saveFormData, relationship, stepKey, pathname, router]);
  const handleBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // Determine if back button should be disabled based on step clickability conditions
  // These match the conditions used in progress-step-item.tsx for step clickability
  const isBackDisabled = () => {
    if (!navigationState.canGoBack) return true;
    const currentStepId = stepConfig.id;
    // When unit is confirmed (sellerUnitId exists), disable back for steps >= 7
    const hasConfirmedUnit = sellerUnitId && currentStepId >= 7;
    // When unit is not confirmed but saved (rawUnitId exists), disable back for steps >= 7
    const hasSavedUnit = !sellerUnitId && rawUnitId && currentStepId >= 7;
    // When no unit exists, disable back for steps >= 3
    const hasNoUnit = !sellerUnitId && !rawUnitId && currentStepId >= 3;
    return hasConfirmedUnit || hasSavedUnit || hasNoUnit;
  };

  // Step restriction logic to prevent manual URL manipulation
  useEffect(() => {
    if (isInitializing || !stepConfig.id || !stepKey) return;
    let isAllowed = false;
    let fallbackStepKey = "profile";
    if (sellerUnitId) {
      // When unit is confirmed, only steps 7 (fee), 8 (slot), and 9 (success) are allowed
      isAllowed = stepConfig.id >= 7;
      fallbackStepKey = "fee";
    } else if (rawUnitId) {
      // When draft exists, steps 1-7 are allowed
      isAllowed = stepConfig.id <= 7;
      fallbackStepKey = "profile";
    } else if (urlGroupId) {
      // When only group exists, steps 1-3 are allowed (profile, address, basic)
      isAllowed = stepConfig.id <= 3;
      fallbackStepKey = "profile";
    } else {
      // Initially only step 1 (profile) is allowed
      isAllowed = stepConfig.id <= 1;
      fallbackStepKey = "profile";
    }
    if (!isAllowed) {
      const fallbackStep = stepConfigs.find(s => s.key === fallbackStepKey) || stepConfigs[0];
      const params_1 = new URLSearchParams(searchParams?.toString() || "");
      params_1.set("process", fallbackStep.key);
      toast.warning(`Step "${toSentenceCase(stepKey)}" is not accessible at this stage. Redirecting...`);
      router.replace(`${pathname}?${params_1.toString()}`);
    }
  }, [stepConfig.id, stepKey, sellerUnitId, rawUnitId, urlGroupId, isInitializing, stepConfigs, router, pathname, searchParams]);
  const disableBack = isBackDisabled();
  const rawUnitData = Object.values(state).reduce((acc, cur) => {
    return {
      ...acc,
      ...cur
    };
  }, {});
  if (stepKey !== "profile" && !sellerGroupId && !rawUnitId && !sellerUnitId) router.push("/sell?process=profile");

  // Get the current step component
  const StepComponent = stepComponents[stepKey as keyof typeof stepComponents];
  let errorMessage = "";
  if (isInitializing || isLoading) {
    errorMessage = "Loading...";
  } else if (!isAuthenticated && (rawUnitId || sellerGroupId || sellerUnitId)) {
    errorMessage = "You are not authorized to access this page. Please log in to continue.";
  } else if (initializationError) {
    errorMessage = initializationError?.message || "Failed to load form data. Please refresh the page.";
  } else if (!stepComponents) {
    errorMessage = "Invalid step configuration";
  }

  // Error state
  if (errorMessage) {
    return <PostPropertyLayout stepConfig={stepConfig} navigationState={navigationState} rawUnitData={rawUnitData} steps={stepConfigs} urlGroupId={sellerGroupId} rawUnitId={rawUnitId} sellerUnitId={sellerUnitId} onBack={handleBack} disableBack={disableBack}>
				<div className="flex flex-col items-center justify-center min-h-[60vh] gap-2 p-4 text-center">
					<div className="grid place-items-center rounded-full bg-destructive/10 p-2">
						<svg className="h-10 w-10 text-destructive" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
							<circle cx="12" cy="12" r="10" />
							<line x1="12" x2="12" y1="8" y2="12" />
							<line x1="12" x2="12.01" y1="16" y2="16" />
						</svg>
					</div>
					<h3 className="text-xl font-semibold tracking-tight text-destructive">{errorMessage}</h3>
				</div>
			</PostPropertyLayout>;
  }
  const stepProps = {
    rawUnitId: rawUnitId,
    sellerUnitId: sellerUnitId,
    groupId: sellerGroupId,
    urlGroupId: urlGroupId,
    process: process,
    onNext: handleNext,
    onBack: handleBack,
    isLoading: isSaving
  };
  return <PostPropertyLayout stepConfig={stepConfig} navigationState={navigationState} rawUnitData={rawUnitData} steps={stepConfigs} rawUnitId={rawUnitId} urlGroupId={sellerGroupId} sellerUnitId={sellerUnitId} onBack={handleBack} disableBack={disableBack} onStepClick={navigateToStep}>
			{/* Use stepKey as key to prevent remounting when parent re-renders */}
			<StepComponent key={stepKey} {...stepProps} />
		</PostPropertyLayout>;
}